import {
  useContext,
  PropsWithChildren,
  createContext,
  useEffect,
  useReducer,
  useCallback,
} from "react"
import { SalaryCalculation } from "@oysterhr/schema"
import { CountryFormData, QuoteCycle } from "../types"
import { useCalculationsParams } from "../hooks"

export interface CalculationsContextType {
  calculations: Array<SalaryCalculation>
  addCalculation: (calculation: SalaryCalculation) => void
  setCalculations: (calculations: SalaryCalculation[], activeIndex?: number) => void
  deleteCalculation: (index: number) => void
  editCalculation: (calculation: SalaryCalculation, index: number) => void
  isEditMode: boolean
  setIsEditMode: (isEditMode: boolean, index?: number) => void
  isCalculationFormOpen: boolean
  setIsCalculationFormOpen: (isOpen: boolean) => void
  cycle: QuoteCycle
  setCycle: (cycle: QuoteCycle) => void
  setActiveCalculationIndex: (index: number) => void
  activeCalculationIndex: number
  multiSelectOptions?: CountryFormData
  activeCalculation?: SalaryCalculation
  hireUrl?: string
  // Amplitude event prefix
  eventPrefix?: string
  companyCurrencyCode?: string
}

type CalculationsProviderProps = PropsWithChildren<{
  multiSelectOptions?: CountryFormData
  hireUrl?: string
  eventPrefix?: string
  companyCurrencyCode?: string
}>

enum CalculationActionType {
  SET_CALCULATIONS = "SET_CALCULATIONS",
  ADD_CALCULATION = "ADD_CALCULATION",
  DELETE_CALCULATION = "DELETE_CALCULATION",
  EDIT_CALCULATION = "EDIT_CALCULATION",
  SET_ACTIVE_INDEX = "SET_ACTIVE_INDEX",
  SET_CYCLE = "SET_CYCLE",
  SET_IS_CALCULATION_FORM_OPEN = "SET_IS_CALCULATION_FORM_OPEN",
  SET_IS_EDIT_MODE = "SET_IS_EDIT_MODE",
}

type CalculationsState = {
  activeCalculationIndex: number
  cycle: QuoteCycle
  calculations: SalaryCalculation[]
  isCalculationFormOpen: boolean
  isEditMode: boolean
}

type CalculationsAction =
  | {
      type: CalculationActionType.ADD_CALCULATION
      payload: SalaryCalculation
    }
  | {
      type: CalculationActionType.SET_CALCULATIONS
      payload: { calculations: SalaryCalculation[]; activeIndex?: number }
    }
  | {
      type: CalculationActionType.DELETE_CALCULATION | CalculationActionType.SET_ACTIVE_INDEX
      payload: number
    }
  | {
      type: CalculationActionType.EDIT_CALCULATION
      payload: { updatedCalculation: SalaryCalculation; index: number }
    }
  | {
      type: CalculationActionType.SET_CYCLE
      payload: QuoteCycle
    }
  | {
      type: CalculationActionType.SET_IS_CALCULATION_FORM_OPEN
      payload: boolean
    }
  | {
      type: CalculationActionType.SET_IS_EDIT_MODE
      payload: { isEditMode: boolean; index?: number }
    }

const CalculationsContext = createContext<CalculationsContextType | null>(null)

function calculationsReducer(
  state: CalculationsState,
  action: CalculationsAction,
): CalculationsState {
  switch (action.type) {
    /**
     * If the new list of calculations is not empty and an index was not provided, set the first calculation as selected
     */
    case CalculationActionType.SET_CALCULATIONS: {
      const { calculations, activeIndex } = action.payload
      const updatedActiveIndex = activeIndex ?? (calculations.length ? 0 : -1)
      return {
        ...state,
        calculations: calculations,
        activeCalculationIndex: updatedActiveIndex,
      }
    }
    case CalculationActionType.ADD_CALCULATION: {
      const updatedCalculations = [...state.calculations, action.payload]
      return {
        ...state,
        calculations: updatedCalculations,
        activeCalculationIndex: updatedCalculations.length - 1,
      }
    }
    case CalculationActionType.EDIT_CALCULATION: {
      const { updatedCalculation, index } = action.payload
      const updatedCalculations = state.calculations.map((calculation, i) =>
        i === index ? updatedCalculation : calculation,
      )
      return {
        ...state,
        calculations: updatedCalculations,
      }
    }
    /**
     * Delete a calculation from the list of calculations.
     * If we delete the active calculation, we need to set the active calculation to the next one in the list.
     * If we delete the last calculation, we need to set the active calculation to the previous one in the list.
     */
    case CalculationActionType.DELETE_CALCULATION: {
      const deletedIndex = action.payload
      const { calculations, activeCalculationIndex } = state

      const updatedCalculations = calculations.filter((_calc, i) => i !== deletedIndex)

      const isLastCalculation = deletedIndex === calculations.length - 1
      const isOnlyCalculation = calculations.length === 1
      let updatedActiveIndex = activeCalculationIndex

      if (isOnlyCalculation) updatedActiveIndex = -1
      else if (activeCalculationIndex > deletedIndex)
        updatedActiveIndex = activeCalculationIndex - 1
      else if (activeCalculationIndex === deletedIndex && isLastCalculation) {
        updatedActiveIndex = activeCalculationIndex - 1
      }

      return {
        ...state,
        calculations: updatedCalculations,
        activeCalculationIndex: updatedActiveIndex,
      }
    }
    case CalculationActionType.SET_ACTIVE_INDEX: {
      return { ...state, activeCalculationIndex: action.payload }
    }
    case CalculationActionType.SET_CYCLE: {
      return { ...state, cycle: action.payload }
    }
    case CalculationActionType.SET_IS_CALCULATION_FORM_OPEN: {
      return { ...state, isCalculationFormOpen: action.payload }
    }
    case CalculationActionType.SET_IS_EDIT_MODE: {
      const { isEditMode, index } = action.payload
      return {
        ...state,
        isCalculationFormOpen: isEditMode,
        isEditMode: isEditMode,
        activeCalculationIndex: index !== undefined ? index : state.activeCalculationIndex,
      }
    }
    default:
      return state
  }
}

export const CalculationsProvider = ({
  children,
  multiSelectOptions,
  hireUrl,
  eventPrefix,
  companyCurrencyCode,
}: CalculationsProviderProps) => {
  const { params, resetParams, updateURLWithCalculations } = useCalculationsParams()
  const [state, dispatch] = useReducer(calculationsReducer, {
    calculations: [],
    activeCalculationIndex: params.get("activeCalculationIndex")
      ? Number(params.get("activeCalculationIndex"))
      : -1,
    cycle: params.get("cycle") ? (params.get("cycle") as QuoteCycle) : "annual",
    isCalculationFormOpen: params.get("countryCode") ? false : true,
    isEditMode: false,
  })

  const { calculations, activeCalculationIndex, cycle, isCalculationFormOpen, isEditMode } = state

  const addCalculation = useCallback(
    (calculation: SalaryCalculation) => {
      dispatch({ type: CalculationActionType.ADD_CALCULATION, payload: calculation })
    },
    [dispatch],
  )

  const deleteCalculation = useCallback(
    (index: number) => {
      // If last calculation, reset URL
      if (calculations.length === 1) {
        resetParams()
      }
      dispatch({ type: CalculationActionType.DELETE_CALCULATION, payload: index })
    },
    [dispatch, resetParams, calculations],
  )

  const editCalculation = useCallback(
    (updatedCalculation: SalaryCalculation, index: number) => {
      dispatch({
        type: CalculationActionType.EDIT_CALCULATION,
        payload: { updatedCalculation, index },
      })
    },
    [dispatch],
  )

  const setCycle = useCallback(
    (cycle: QuoteCycle) => {
      dispatch({ type: CalculationActionType.SET_CYCLE, payload: cycle })
    },
    [dispatch],
  )

  const setCalculations = useCallback(
    (calculations: SalaryCalculation[], activeIndex?: number) => {
      if (calculations.length === 0) {
        resetParams()
      }
      dispatch({
        type: CalculationActionType.SET_CALCULATIONS,
        payload: { calculations, activeIndex },
      })
    },
    [dispatch, resetParams],
  )

  const setActiveCalculationIndex = useCallback(
    (activeIndex: number) => {
      dispatch({ type: CalculationActionType.SET_ACTIVE_INDEX, payload: activeIndex })
    },
    [dispatch],
  )

  const setIsCalculationFormOpen = useCallback(
    (isFormOpen: boolean) => {
      dispatch({ type: CalculationActionType.SET_IS_CALCULATION_FORM_OPEN, payload: isFormOpen })
    },
    [dispatch],
  )

  const setIsEditMode = useCallback(
    (isEditMode: boolean, index?: number) => {
      dispatch({ type: CalculationActionType.SET_IS_EDIT_MODE, payload: { isEditMode, index } })
    },
    [dispatch],
  )

  const activeCalculation =
    activeCalculationIndex !== -1 ? calculations[activeCalculationIndex] : undefined

  /**
   * We build the URL as a side effect of the local state changing
   */

  useEffect(() => {
    updateURLWithCalculations(calculations, cycle, activeCalculationIndex)
  }, [updateURLWithCalculations, calculations, cycle, activeCalculationIndex])

  return (
    <CalculationsContext.Provider
      value={{
        calculations,
        isCalculationFormOpen,
        isEditMode,
        setIsEditMode,
        setIsCalculationFormOpen,
        addCalculation,
        setCalculations,
        deleteCalculation,
        editCalculation,
        eventPrefix,
        multiSelectOptions,
        cycle,
        setCycle,
        hireUrl,
        activeCalculationIndex,
        setActiveCalculationIndex,
        activeCalculation,
        companyCurrencyCode,
      }}
    >
      {children}
    </CalculationsContext.Provider>
  )
}

export const useCalculationsProvider = () => {
  const ctx = useContext(CalculationsContext)

  if (!ctx) {
    throw new Error("useCalculationsProvider must be used within a CalculationsProvider")
  }

  return ctx
}
